import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Download, Save } from 'lucide-react';
import { SubmissionBadge, downloadSubmissionFile } from './HomeworkPage';
import { QuickGradingGrid } from './QuickGradingGrid';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const toLocalInput = (d) => {
  if (!d) return '';
  const x = new Date(d);
  return new Date(x.getTime() - x.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const SettingsForm = ({ hw, onSaved }) => {
  const [f, setF] = useState({ submissions_open: !!hw.submissions_open, deadline: toLocalInput(hw.submission_deadline), accept_late: hw.accept_late ?? true, instructions: hw.instructions || '' });
  useEffect(() => {
    setF({ submissions_open: !!hw.submissions_open, deadline: toLocalInput(hw.submission_deadline), accept_late: hw.accept_late ?? true, instructions: hw.instructions || '' });
  }, [hw]);
  const save = async () => {
    try {
      await axios.put(`${API}/homework/${hw.id}/settings`, { submissions_open: f.submissions_open, submission_deadline: new Date(f.deadline).toISOString(), accept_late: f.accept_late, instructions: f.instructions || null });
      toast.success('Paramètres du dépôt enregistrés');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Enregistrement impossible');
    }
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="homework-settings-form">
      <div className="flex items-center justify-between border rounded-md p-3">
        <Label>Dépôt en ligne ouvert</Label>
        <Switch checked={f.submissions_open} onCheckedChange={(v) => setF({ ...f, submissions_open: v })} data-testid="homework-open-switch" />
      </div>
      <div className="flex items-center justify-between border rounded-md p-3">
        <div><Label>Accepter les rendus en retard</Label><p className="text-xs text-gray-500">Ils seront signalés « en retard ». Sinon, le dépôt est refusé après la date limite.</p></div>
        <Switch checked={f.accept_late} onCheckedChange={(v) => setF({ ...f, accept_late: v })} data-testid="homework-late-switch" />
      </div>
      <div className="space-y-1"><Label>Date limite</Label>
        <Input type="datetime-local" value={f.deadline} onChange={(e) => setF({ ...f, deadline: e.target.value })} data-testid="homework-deadline-input" /></div>
      <div className="space-y-1"><Label>Consignes</Label>
        <Input value={f.instructions} onChange={(e) => setF({ ...f, instructions: e.target.value })} placeholder="Format attendu, pages…" data-testid="homework-instructions-input" /></div>
      <Button onClick={save} disabled={!f.deadline} className="md:col-span-2" data-testid="homework-settings-save-btn"><Save className="w-4 h-4 mr-2" /> Enregistrer</Button>
    </div>
  );
};

const GradeForm = ({ row, maxGrade, onSaved }) => {
  const fb = row.submission.feedback;
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ grade: fb?.grade ?? '', comment: fb?.comment || '', count: fb ? fb.count_in_average : true });
  const save = async () => {
    try {
      const { data } = await axios.post(`${API}/homework/submissions/${row.submission.id}/grade`, {
        grade: f.grade === '' ? null : Number(f.grade), comment: f.comment || null, count_in_average: f.count,
      });
      toast.success(data.message);
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Correction impossible');
    }
  };
  if (!open) {
    return (
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid={`grade-homework-btn-${row.student_id}`}>
        {fb ? `${fb.grade != null ? `${fb.grade}/${fb.max_grade}` : 'Commenté'} · modifier` : 'Corriger'}
      </Button>
    );
  }
  return (
    <div className="w-full mt-2 grid grid-cols-1 md:grid-cols-6 gap-2 items-center" data-testid={`grade-form-${row.student_id}`}>
      <Input type="number" min="0" max={maxGrade} step="0.25" placeholder={`/ ${maxGrade}`} value={f.grade}
        onChange={(e) => setF({ ...f, grade: e.target.value })} data-testid={`grade-input-${row.student_id}`} />
      <Input className="md:col-span-3" placeholder="Commentaire pour l’élève" value={f.comment}
        onChange={(e) => setF({ ...f, comment: e.target.value })} data-testid={`grade-comment-${row.student_id}`} />
      <label className="flex items-center gap-2 text-xs">
        <Switch checked={f.count} onCheckedChange={(v) => setF({ ...f, count: v })} data-testid={`grade-count-switch-${row.student_id}`} />
        Compte dans la moyenne
      </label>
      <Button size="sm" onClick={save} data-testid={`grade-save-${row.student_id}`}>Envoyer</Button>
    </div>
  );
};

const Roster = ({ data, canGrade, onSaved }) => (
  <div className="space-y-2" data-testid="homework-roster">
    <div className="flex gap-2 text-sm">
      <Badge className="bg-green-100 text-green-800">{data.counts.ON_TIME} à temps</Badge>
      <Badge className="bg-amber-100 text-amber-800">{data.counts.LATE} en retard</Badge>
      <Badge className="bg-gray-100 text-gray-700">{data.counts.MISSING} non rendus</Badge>
    </div>
    {data.rows.map((r) => (
      <div key={r.student_id} className="flex flex-wrap items-center justify-between border rounded-md px-3 py-2 text-sm" data-testid={`roster-row-${r.student_id}`}>
        <span className="font-medium">{r.student_name}</span>
        <span className="flex items-center gap-3">
          {r.submission && <span className="text-gray-500">{new Date(r.submission.submitted_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}</span>}
          {r.submission?.files.map((f) => (
            <button key={f.id} type="button" className="text-blue-700 hover:underline flex items-center gap-1" onClick={() => downloadSubmissionFile(r.submission.id, f)}>
              <Download className="w-3 h-3" />{f.filename}
            </button>
          ))}
          <SubmissionBadge sub={r.submission} />
          {canGrade && r.submission && <GradeForm row={r} maxGrade={data.assessment.max_grade || 20} onSaved={onSaved} />}
        </span>
      </div>
    ))}
  </div>
);

export default function TeacherHomework({ user }) {
  const canConfigure = ['TEACHER', 'ADMIN'].includes(user?.role);
  const [mode, setMode] = useState('list');
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/assessments`).then((r) => setList(r.data.filter((a) => a.assessment_type === 'HOMEWORK'))).catch(() => {});
  }, []);
  const load = (id = selected) => id && axios.get(`${API}/homework/${id}/submissions`).then((r) => setData(r.data)).catch((e) => toast.error(e.response?.data?.detail || 'Chargement impossible'));
  useEffect(() => { load(selected); }, [selected]);

  return (
    <div className="p-6 space-y-4" data-testid="teacher-homework-page">
      <h1 className="text-2xl font-bold">Dépôt des devoirs</h1>
      <Card>
        <CardHeader>
          <CardTitle>Choisir un devoir</CardTitle>
          <CardDescription>Les devoirs sont les évaluations de type « Devoir » créées dans Notes. Ouvrez le dépôt, fixez la date limite et décidez si les retards sont acceptés.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger className="max-w-md" data-testid="teacher-homework-select"><SelectValue placeholder={list.length ? 'Sélectionner' : 'Aucun devoir créé'} /></SelectTrigger>
            <SelectContent>{list.map((a) => <SelectItem key={a.id} value={a.id}>{a.title}</SelectItem>)}</SelectContent>
          </Select>
          {data && canConfigure && <SettingsForm hw={data.assessment} onSaved={() => load()} />}
        </CardContent>
      </Card>
      {data && (
        <Card><CardContent className="pt-6 space-y-4">
          {canConfigure && (
            <div className="flex gap-2">
              <Button size="sm" variant={mode === 'list' ? 'default' : 'outline'} onClick={() => setMode('list')} data-testid="homework-mode-list">Liste des rendus</Button>
              <Button size="sm" variant={mode === 'grid' ? 'default' : 'outline'} onClick={() => setMode('grid')} data-testid="homework-mode-grid">Correction rapide</Button>
            </div>
          )}
          {mode === 'grid' && canConfigure
            ? <QuickGradingGrid data={data} onSaved={() => load()} />
            : <Roster data={data} canGrade={canConfigure} onSaved={() => load()} />}
        </CardContent></Card>
      )}
    </div>
  );
}
